import * as z from 'zod';
import { companySchema, readCompanyRefSchema } from '../companies';
import { readOrganizationRefSchema } from '../organizations';
import { readImageSchema, updateImageSchema } from '../images';

// 1. Reference schema with label field (for dropdowns, selects, links).
// A buyer has no name of its own, so the company is its display label.
export const readBuyerRefSchema = z.object({
  id: z.uuid(),
  company: readCompanyRefSchema,
});
export type ReadBuyerRef = z.infer<typeof readBuyerRefSchema>;

// 2. Minimal reference schema (for foreign keys)
export const buyerRefSchema = z.object({
  id: z.uuid(),
});
export type BuyerRef = z.infer<typeof buyerRefSchema>;

// 3. Read schema - all properties nullable (API response).
// Default fields: id,company,organization,created_at,updated_at
export const readBuyerSchema = z.object({
  id: z.uuid().nullable(),
  company: companySchema.nullable(),
  organization: readOrganizationRefSchema.nullable(),
  // Company logo variants: full logo vs brandmark, in every combination of
  // greyscale and dark-theme rendering.
  logo: z.object(readImageSchema).nullable(),
  brandmarkLogo: z.object(readImageSchema).nullable(),
  logoGreyscale: z.object(readImageSchema).nullable(),
  brandmarkLogoGreyscale: z.object(readImageSchema).nullable(),
  logoDark: z.object(readImageSchema).nullable(),
  brandmarkLogoDark: z.object(readImageSchema).nullable(),
  logoGreyscaleDark: z.object(readImageSchema).nullable(),
  brandmarkLogoGreyscaleDark: z.object(readImageSchema).nullable(),
  createdAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime().nullable(),
});
export type ReadBuyer = z.infer<typeof readBuyerSchema>;

// 6. Patch schema - partial update. Only the logo variants are patchable from
// the frontend (e.g. uploading a logo Tinct could not find). Nullable so a
// variant can be cleared.
export const patchBuyerSchema = z.object({
  logo: updateImageSchema.nullish(),
  brandmarkLogo: updateImageSchema.nullish(),
  logoGreyscale: updateImageSchema.nullish(),
  brandmarkLogoGreyscale: updateImageSchema.nullish(),
  logoDark: updateImageSchema.nullish(),
  brandmarkLogoDark: updateImageSchema.nullish(),
  logoGreyscaleDark: updateImageSchema.nullish(),
  brandmarkLogoGreyscaleDark: updateImageSchema.nullish(),
});
export type PatchBuyer = z.infer<typeof patchBuyerSchema>;

// Legacy export for backward compatibility
export const buyerSchema = readBuyerSchema;
export type Buyer = ReadBuyer;

// === Import =================================================================
// Mirrors the audience/lead import logic: each item references a company,
// either by an existing id or by domain (server resolves/creates it).

export const buyerImportByCompanyIdSchema = z.object({
  company: z.object({
    id: z.string().uuid('Company ID must be a valid UUID'),
  }),
});

export const buyerImportByDomainSchema = z.object({
  company: z.object({
    domain: z.string().min(1, 'Domain is required'),
  }),
});

export const buyerImportSchema = z.union([
  buyerImportByCompanyIdSchema,
  buyerImportByDomainSchema,
]);
export type BuyerImport = z.infer<typeof buyerImportSchema>;

// Import context for buyers. Buyers are scoped to the organization via the
// X-Tinct-OrganizationId header (auto-injected by the api client), so the
// context carries no extra parent reference.
export const importBuyerContextSchema = z.object({});
export type ImportBuyerContext = z.infer<typeof importBuyerContextSchema>;

// Buyer import item (for simple list import)
export const importBuyerItemSchema = z.object({
  company: z
    .object({
      id: z.string().optional(),
      domain: z.string().optional(),
      name: z.string().optional(),
    })
    .optional(),
});
export type ImportBuyerItem = z.infer<typeof importBuyerItemSchema>;
